import { useState, useRef, useEffect } from "react";
import { X, Send, MessageCircle } from "lucide-react";
import { Button } from "../ui/button";
import Text from "./Text";
import { useChatMutation } from "@/hooks/mutations";
import { useGetRolesQuery } from "@/hooks/queries";
import LLMOutput from "./LLMOutput";

type Message = {
  id: string;
  text: string;
  sender: "user" | "ai";
  timestamp: Date;
};

type ChatInterfaceProps = {
  isOpen: boolean;
  onClose: () => void;
  insightId: string;
  scheduledJobId: string;
};

const ChatInterface: React.FC<ChatInterfaceProps> = ({
  isOpen,
  onClose,
  insightId,
  scheduledJobId,
}) => {
  const { data: roleData } = useGetRolesQuery();
  const [messages, setMessages] = useState<Message[]>([
    {
      id: "1",
      text: "Hello! I'm your AI assistant. How can I help you with your insights today?",
      sender: "ai",
      timestamp: new Date(),
    },
  ]);
  const [inputValue, setInputValue] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Check if user is super admin
  const isSuperAdmin = roleData?.isSuperAdmin;
  const isAdmin = roleData?.isAdmin;

  const { mutate: sendChat, isPending } = useChatMutation({
    onSuccess: (data) => {
      const aiMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: data.response,
        sender: "ai",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, aiMessage]);
    },
    onError: (error) => {
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: `Sorry, I encountered an error: ${error.message}. Please try again.`,
        sender: "ai",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    },
  });

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isPending) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      text: inputValue,
      sender: "user",
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    const query = inputValue;
    setInputValue("");

    // Send to API
    sendChat({
      insightId,
      scheduledJobId,
      query,
    });
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  if (!isOpen) return null;

  // If user is not super admin, don't render the chat interface
  if (!isSuperAdmin && !isAdmin) {
    return (
      <div className="fixed bottom-20 right-6 z-50 flex h-[700px] w-[600px] max-h-[90vh] max-w-[90vw] flex-col rounded-lg border border-shades-neutral-50 bg-white shadow-2xl sm:h-[600px] sm:w-[500px] md:h-[700px] md:w-[600px]">
        <div className="flex items-center justify-between border-b border-shades-neutral-50 p-4">
          <div className="flex items-center gap-2">
            <MessageCircle className="h-5 w-5 text-blue-600" />
            <Text variant="heading_h6" className="font-semibold">
              AI Assistant
            </Text>
          </div>
          <button
            onClick={onClose}
            className="rounded-full p-1 hover:bg-gray-100 transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="text-center">
            <MessageCircle className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <Text variant="heading_h6" className="text-gray-600 mb-2">
              Access Restricted
            </Text>
            <Text variant="normalText" className="text-gray-500">
              AI Chat is only available for Super Admin users.
            </Text>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed bottom-20 right-6 z-50 flex h-[700px] w-[600px] max-h-[90vh] max-w-[90vw] flex-col rounded-lg border border-shades-neutral-50 bg-white shadow-2xl sm:h-[600px] sm:w-[500px] md:h-[700px] md:w-[600px]">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-shades-neutral-50 p-4">
        <div className="flex items-center gap-2">
          <MessageCircle className="h-5 w-5 text-blue-600" />
          <Text variant="heading_h6" className="font-semibold">
            AI Assistant
          </Text>
        </div>
        <button
          onClick={onClose}
          className="rounded-full p-1 hover:bg-gray-100 transition-colors"
        >
          <X className="h-5 w-5 text-gray-500" />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex ${
              message.sender === "user" ? "justify-end" : "justify-start"
            }`}
          >
            <div
              className={`max-w-[95%] rounded-lg px-4 py-3 ${
                message.sender === "user"
                  ? "bg-blue-600 text-white"
                  : "bg-gray-100 text-gray-900"
              }`}
            >
              {message.sender === "user" ? (
                <Text
                  variant="normalText"
                  className="text-white whitespace-pre-wrap"
                >
                  {message.text}
                </Text>
              ) : (
                <LLMOutput content={message.text} />
              )}
              <Text
                variant="normalText"
                className={`mt-2 text-xs ${
                  message.sender === "user"
                    ? "text-blue-100"
                    : "text-gray-500"
                }`}
              >
                {message.timestamp.toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </Text>
            </div>
          </div>
        ))}
        {isPending && (
          <div className="flex justify-start">
            <div className="bg-gray-100 rounded-lg px-4 py-2">
              <div className="flex space-x-1">
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" />
                <div
                  className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                  style={{ animationDelay: "0.1s" }}
                />
                <div
                  className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                  style={{ animationDelay: "0.2s" }}
                />
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="border-t border-shades-neutral-50 p-4">
        <div className="flex gap-2">
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type your message..."
            className="flex-1 rounded-lg border border-shades-neutral-50 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <Button
            onClick={handleSendMessage}
            disabled={!inputValue.trim() || isPending}
            className="bg-blue-600 hover:bg-blue-700 h-10 w-10 p-0"
            isLoading={isPending}
          >
            {!isPending && <Send className="h-4 w-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ChatInterface;

